import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash, faPaperclip, faXmark, faThumbtack } from '@fortawesome/free-solid-svg-icons';
import {
  fetchEventNotes,
  createEventNote,
  updateEventNote,
  deleteEventNote,
  setEventNotePinned,
  sortEventNotes,
  uploadEventNoteAttachment,
  voidEventNoteAttachment,
  unvoidEventNoteAttachment,
  eventNoteAttachmentDownloadUrl,
  type EventNoteAttachmentRow,
  type EventNoteRow,
} from '../api/eventNotes';
import AttachmentVoidModal from './AttachmentVoidModal';
import { AttachmentVoidPill, AttachmentVoidReason } from './AttachmentVoidNote';
import { useAttachmentVoid } from '../hooks/useAttachmentVoid';
import { isVoided } from '../api/attachmentVoid';
import './EventNotesTab.css';

interface EventNotesTabProps {
  eventId: number;
  viewerUserId: number;
}

function formatNoteTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function noteWasEdited(note: EventNoteRow): boolean {
  if (!note.updated_at || !note.created_at) return false;
  return new Date(note.updated_at).getTime() - new Date(note.created_at).getTime() > 1000;
}

function authorInitial(name: string | null): string {
  const n = (name ?? '').trim();
  return n ? n.charAt(0).toUpperCase() : '?';
}

const EventNotesTab: React.FC<EventNotesTabProps> = ({ eventId, viewerUserId }) => {
  const [notes, setNotes] = useState<EventNoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newNote, setNewNote] = useState('');
  const [newFile, setNewFile] = useState<File | null>(null);
  const [posting, setPosting] = useState(false);
  const composerFileInputRef = useRef<HTMLInputElement>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [busyDeleteId, setBusyDeleteId] = useState<number | null>(null);
  const [pinBusyId, setPinBusyId] = useState<number | null>(null);
  const [attachBusyNoteId, setAttachBusyNoteId] = useState<number | null>(null);
  const [rowError, setRowError] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setNotes(sortEventNotes(await fetchEventNotes(eventId)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load notes.');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleAdd = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const text = newNote.trim();
      if (!text || posting) return;
      setPosting(true);
      setError('');
      try {
        const created = await createEventNote(eventId, { note: text, user_id: viewerUserId });
        let withAttachment = created;
        if (newFile) {
          try {
            const att = await uploadEventNoteAttachment(eventId, created.id, newFile, viewerUserId);
            withAttachment = { ...created, attachments: [att] };
          } catch (attErr) {
            setError(attErr instanceof Error ? attErr.message : 'Note saved, but the file failed to upload.');
          }
        }
        setNotes((prev) => sortEventNotes([...prev, withAttachment]));
        setNewNote('');
        setNewFile(null);
        if (composerFileInputRef.current) composerFileInputRef.current.value = '';
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add note.');
      } finally {
        setPosting(false);
      }
    },
    [newNote, newFile, posting, eventId, viewerUserId]
  );

  const startEdit = useCallback((note: EventNoteRow) => {
    setEditingId(note.id);
    setEditText(note.note);
    setRowError('');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditText('');
    setRowError('');
  }, []);

  const saveEdit = useCallback(
    async (noteId: number) => {
      const text = editText.trim();
      if (!text || savingEdit) return;
      setSavingEdit(true);
      setRowError('');
      try {
        const updated = await updateEventNote(eventId, noteId, { note: text, user_id: viewerUserId });
        setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...updated, attachments: n.attachments } : n)));
        setEditingId(null);
        setEditText('');
      } catch (err) {
        setRowError(err instanceof Error ? err.message : 'Failed to update note.');
      } finally {
        setSavingEdit(false);
      }
    },
    [editText, savingEdit, eventId, viewerUserId]
  );

  const handleDelete = useCallback(
    async (noteId: number) => {
      if (busyDeleteId != null) return;
      if (!window.confirm('Delete this note? This cannot be undone.')) return;
      setBusyDeleteId(noteId);
      setRowError('');
      try {
        await deleteEventNote(eventId, noteId, viewerUserId);
        setNotes((prev) => prev.filter((n) => n.id !== noteId));
      } catch (err) {
        setRowError(err instanceof Error ? err.message : 'Failed to delete note.');
      } finally {
        setBusyDeleteId(null);
      }
    },
    [busyDeleteId, eventId, viewerUserId]
  );

  const handleTogglePin = useCallback(
    async (note: EventNoteRow) => {
      if (pinBusyId != null) return;
      setPinBusyId(note.id);
      setRowError('');
      try {
        const updated = await setEventNotePinned(eventId, note.id, {
          pinned: !note.is_pinned,
          user_id: viewerUserId,
        });
        setNotes((prev) =>
          sortEventNotes(
            prev.map((n) => (n.id === note.id ? { ...updated, attachments: n.attachments } : n))
          )
        );
      } catch (err) {
        setRowError(err instanceof Error ? err.message : 'Failed to update the pin.');
      } finally {
        setPinBusyId(null);
      }
    },
    [pinBusyId, eventId, viewerUserId]
  );

  const handleAttachToNote = useCallback(
    async (noteId: number, file: File | undefined) => {
      if (!file) return;
      setAttachBusyNoteId(noteId);
      setRowError('');
      try {
        const att = await uploadEventNoteAttachment(eventId, noteId, file, viewerUserId);
        setNotes((prev) =>
          prev.map((n) => (n.id === noteId ? { ...n, attachments: [...(n.attachments ?? []), att] } : n))
        );
      } catch (err) {
        setRowError(err instanceof Error ? err.message : 'Failed to upload file.');
      } finally {
        setAttachBusyNoteId(null);
      }
    },
    [eventId, viewerUserId]
  );

  /** Merges the void columns back onto one of a note's attachments. */
  const applyAttachmentUpdate = useCallback((att: EventNoteAttachmentRow) => {
    setNotes((prev) =>
      prev.map((n) =>
        n.id !== att.event_note_id
          ? n
          : { ...n, attachments: (n.attachments ?? []).map((a) => (a.id === att.id ? { ...a, ...att } : a)) }
      )
    );
  }, []);

  const attachmentVoid = useAttachmentVoid<EventNoteAttachmentRow>({
    label: (a) => a.original_file_name,
    onVoid: async (att, reason) => {
      setRowError('');
      applyAttachmentUpdate(await voidEventNoteAttachment(eventId, att.event_note_id, att.id, reason, viewerUserId));
    },
    onUnvoid: async (att) => {
      setRowError('');
      applyAttachmentUpdate(await unvoidEventNoteAttachment(eventId, att.event_note_id, att.id, viewerUserId));
    },
    onError: setRowError,
  });

  return (
    <div className="event-notes-tab">
      {rowError ? (
        <p className="create-order-error" role="alert">
          {rowError}
        </p>
      ) : null}

      {loading ? (
        <p className="event-notes-empty">Loading notes…</p>
      ) : notes.length === 0 ? (
        <p className="event-notes-empty">No notes yet. Be the first to leave one below.</p>
      ) : (
        <ul className="event-notes-list">
          {notes.map((note) => {
            const isAuthor = note.user_id != null && note.user_id === viewerUserId;
            const isEditing = editingId === note.id;
            const attachments = note.attachments ?? [];
            return (
              <li
                key={note.id}
                className={`event-note-card${note.is_pinned ? ' event-note-card--pinned' : ''}`}
              >
                <div className="event-note-avatar" aria-hidden>
                  {authorInitial(note.submitted_by)}
                </div>
                <div className="event-note-body">
                  <div className="event-note-meta">
                    <span className="event-note-author">{note.submitted_by ?? 'Unknown user'}</span>
                    <span className="event-note-time">
                      {formatNoteTime(note.created_at)}
                      {noteWasEdited(note) ? ' · edited' : ''}
                    </span>
                    {note.is_pinned ? (
                      <span
                        className="event-note-pinned-badge"
                        title={note.pinned_by ? `Pinned by ${note.pinned_by}` : 'Pinned'}
                      >
                        <FontAwesomeIcon icon={faThumbtack} aria-hidden /> Pinned
                        {note.pinned_by ? ` by ${note.pinned_by}` : ''}
                      </span>
                    ) : null}
                  </div>

                  {isEditing ? (
                    <div className="event-note-edit">
                      <textarea
                        className="event-notes-textarea"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={3}
                        disabled={savingEdit}
                      />
                      <div className="event-note-edit-actions">
                        <button
                          type="button"
                          className="event-notes-add-btn"
                          onClick={() => void saveEdit(note.id)}
                          disabled={savingEdit || !editText.trim()}
                        >
                          {savingEdit ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="event-note-cancel-btn"
                          onClick={cancelEdit}
                          disabled={savingEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="event-note-text">{note.note}</p>
                  )}

                  {attachments.length > 0 ? (
                    <ul className="event-note-attachments">
                      {attachments.map((att) => (
                        <li
                          key={att.id}
                          className={`event-note-attachment${isVoided(att) ? ' attachment-voided' : ''}`}
                        >
                          <FontAwesomeIcon icon={faPaperclip} className="event-note-attachment-icon" aria-hidden />
                          <a
                            href={eventNoteAttachmentDownloadUrl(eventId, note.id, att.id)}
                            className="event-note-attachment-link attachment-void-name"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {att.original_file_name}
                          </a>
                          {isVoided(att) ? <AttachmentVoidPill /> : null}
                          {isAuthor ? (
                            <button
                              type="button"
                              className={isVoided(att) ? 'attachment-unvoid-btn' : 'attachment-void-btn'}
                              disabled={attachmentVoid.busy}
                              onClick={() =>
                                isVoided(att) ? attachmentVoid.unvoid(att) : attachmentVoid.requestVoid(att)
                              }
                            >
                              {isVoided(att) ? 'Unvoid' : 'Void'}
                            </button>
                          ) : null}
                          <AttachmentVoidReason attachment={att} />
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {isAuthor && !isEditing ? (
                    <label className="event-note-attach-more">
                      <FontAwesomeIcon icon={faPaperclip} aria-hidden />
                      {attachBusyNoteId === note.id ? ' Uploading…' : ' Attach file'}
                      <input
                        type="file"
                        hidden
                        disabled={attachBusyNoteId === note.id}
                        onChange={(e) => {
                          void handleAttachToNote(note.id, e.target.files?.[0]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  ) : null}
                </div>

                {!isEditing ? (
                  <div className="event-note-actions">
                    {/* Anyone can pin: it flags the note for the team, it does not change it. */}
                    <button
                      type="button"
                      className={`event-note-action-btn event-note-action-btn--pin${
                        note.is_pinned ? ' event-note-action-btn--pin-on' : ''
                      }`}
                      title={note.is_pinned ? 'Unpin note' : 'Pin note to the top'}
                      aria-label={note.is_pinned ? 'Unpin note' : 'Pin note to the top'}
                      aria-pressed={note.is_pinned}
                      onClick={() => void handleTogglePin(note)}
                      disabled={pinBusyId === note.id}
                    >
                      <FontAwesomeIcon icon={faThumbtack} />
                    </button>
                    {isAuthor ? (
                      <>
                        <button
                          type="button"
                          className="event-note-action-btn event-note-action-btn--edit"
                          title="Edit note"
                          aria-label="Edit note"
                          onClick={() => startEdit(note)}
                        >
                          <FontAwesomeIcon icon={faPenToSquare} />
                        </button>
                        <button
                          type="button"
                          className="event-note-action-btn event-note-action-btn--delete"
                          title="Delete note"
                          aria-label="Delete note"
                          onClick={() => void handleDelete(note.id)}
                          disabled={busyDeleteId === note.id}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {error ? (
        <p className="create-order-error" role="alert">
          {error}
        </p>
      ) : null}

      <form className="event-notes-composer" onSubmit={handleAdd}>
        <label className="event-notes-composer-label" htmlFor="event-note-input">
          Add a note
        </label>
        <textarea
          id="event-note-input"
          className="event-notes-textarea"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Leave a message, note, or comment about this event…"
          rows={3}
          disabled={posting}
        />
        <div className="event-notes-composer-actions">
          <label className="event-note-file-picker">
            <FontAwesomeIcon icon={faPaperclip} aria-hidden />
            <span>{newFile ? newFile.name : 'Attach file'}</span>
            <input
              ref={composerFileInputRef}
              type="file"
              hidden
              disabled={posting}
              onChange={(e) => setNewFile(e.target.files?.[0] ?? null)}
            />
          </label>
          {newFile ? (
            <button
              type="button"
              className="event-note-file-clear"
              onClick={() => {
                setNewFile(null);
                if (composerFileInputRef.current) composerFileInputRef.current.value = '';
              }}
              disabled={posting}
            >
              <FontAwesomeIcon icon={faXmark} aria-hidden /> Remove file
            </button>
          ) : null}
          <button type="submit" className="event-notes-add-btn" disabled={posting || !newNote.trim()}>
            {posting ? 'Posting…' : 'Add note'}
          </button>
        </div>
      </form>

      <AttachmentVoidModal control={attachmentVoid} />
    </div>
  );
};

export default EventNotesTab;
